import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";

type AppRole = "admin" | "partner" | "youth" | "mentor" | "platform_manager";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
  requireApproval?: boolean;
}

const ProtectedRoute = ({ 
  children, 
  allowedRoles,
  requireApproval = true 
}: ProtectedRouteProps) => {
  const { user, profile, roles, isLoading } = useAuth();
  const location = useLocation();

  // Only show skeleton loader during the initial auth bootstrap (no user yet)
  if (!user && isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-10 rounded-full" />
          </div>
          {/* Content skeleton */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
          <Skeleton className="h-64 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // During initial profile/role fetch we must not render protected content.
  // But during background refreshes we also must not unmount the route tree.
  const isBootstrappingAccess = isLoading && (!profile || roles.length === 0);

  if (isBootstrappingAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Background refresh: keep the existing UI mounted to preserve state.
  if (isLoading) {
    return <>{children}</>;
  }

  // Check if profile is pending approval
  if (requireApproval && profile?.status === "pending") {
    return <Navigate to="/pending-approval" replace />;
  }

  // Check if user has required role
  if (allowedRoles && allowedRoles.length > 0) {
    const hasRequiredRole = allowedRoles.some((role) => roles.includes(role));
    if (!hasRequiredRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;